import React, {useCallback, useEffect, useRef, useState} from 'react';
import {View} from 'react-native';
import {Map as MapType} from 'yandex-maps';
import {Clusterer, Map, Placemark, YMaps} from 'react-yandex-maps';
import MapMarker from '../../../../assets/icons/map-marker.svg';
import MapPin from '../../../../assets/icons/map-pin.svg';
import LoadableComponent from '../../loadableContent/view';
import {MapDetails} from '../MapDetails';
import {DEFAULT_CENTER_POINT, DEFAULT_ZOOM_LEVEL, ICON_OFFSET, ICON_SIZE} from '../constants';
import {Marker, YaMapProps} from '../types';
import {useYaMapStyles} from './YaMap.style';

const CORRECTION_RATIO = 0.8;

export function YaMap<T>({
  markers,
  details,
  centerPoint = DEFAULT_CENTER_POINT,
  zoomLevel = DEFAULT_ZOOM_LEVEL,
  isDetailsVisible = false,
  loading = false,
  onCloseDetails,
  onPressMarker,
}: YaMapProps<T>) {
  const [currentMarkerId, setCurrentMarkerId] = useState<number>();
  const [mapIsLoaded, setMapLoaded] = useState<boolean>(false);

  const styles = useYaMapStyles();
  const mapInstance = useRef<MapType>();
  const handleMapInstanceChange = useCallback((instance: unknown) => {
    if (!instance || mapInstance.current != null) return;
    mapInstance.current = instance as MapType;
  }, []);

  const handleMarkerPress = useCallback((marker: Marker<T>) => {
    setCurrentMarkerId(marker.id);
    onPressMarker?.(marker);
  }, []);

  useEffect(() => {
    if (markers && markers.length > 0 && mapInstance.current != null) {
      const lats = markers.map((m) => m.coordinates[0]);
      const lons = markers.map((m) => m.coordinates[1]);
      const minLat = Math.min(...lats);
      const maxLat = Math.max(...lats);
      const minLon = Math.min(...lons);
      const maxLon = Math.max(...lons);

      const bounds = [
        [minLat, minLon],
        [maxLat, maxLon],
      ];

      mapInstance.current.setBounds(bounds);
      if (markers.length === 1) mapInstance.current.setZoom(19);
    }
  }, [markers, mapInstance.current]);

  return (
    <View style={styles.mapView}>
      <LoadableComponent loading={loading}>
        {details && (
          <MapDetails details={details} isDetailsVisible={isDetailsVisible} onCloseDetails={onCloseDetails} />
        )}
        <YMaps style={{overflow: 'hidden'}}>
          <Map
            width='100%'
            height='100%'
            instanceRef={handleMapInstanceChange}
            options={{autoFitToViewport: 'always'}}
            state={{center: centerPoint, zoom: zoomLevel}}
            onLoad={() => setMapLoaded(true)}
            defaultState={{center: DEFAULT_CENTER_POINT, zoom: DEFAULT_ZOOM_LEVEL - CORRECTION_RATIO}}>
            {mapIsLoaded && (
              <Clusterer options={{preset: 'islands#invertedOrangeClusterIcons'}}>
                {markers.map((marker) => (
                  <Placemark
                    key={marker.id}
                    geometry={marker.coordinates}
                    onClick={() => handleMarkerPress(marker)}
                    options={{
                      iconLayout: 'default#image',
                      iconImageHref: marker.id === currentMarkerId && isDetailsVisible ? MapMarker : MapPin,
                      iconImageSize: ICON_SIZE,
                      iconImageOffset: ICON_OFFSET,
                    }}
                  />
                ))}
              </Clusterer>
            )}
          </Map>
        </YMaps>
      </LoadableComponent>
    </View>
  );
}
